import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAtomValue } from 'jotai';
import MainURL from '../../../MainURL';
import { loginStateAtom } from '../../../AtomStore';
import { fetchSongsByThemes, PraiseRecommendSong } from './fetchSongsByThemes';

export type { PraiseRecommendSong };

const MAX_FILE_SIZE = 30 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const DEFAULT_FILE_STATUS = '아직 업로드된 파일이 없습니다. 설교 원문을 선택하면 이 영역에 파일명이 표시됩니다.';

export type SupportTextTabKey = 'summary' | 'qt' | 'groupShare';
export type SupportTabKey = SupportTextTabKey | 'praise';

export const SUPPORT_TAB_LABELS: Record<SupportTabKey, string> = {
  summary: '설교 요약',
  qt: 'QT 묵상',
  groupShare: '소그룹 나눔지',
  praise: 'AI 찬양추천',
};

export interface SermonSupportResultData {
  summary?: string;
  qt?: string;
  groupShare?: string;
  praiseThemes: string[];
  praiseSongs: PraiseRecommendSong[];
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function useSermonSupport() {
  const isLogin = useAtomValue(loginStateAtom);

  const [sermonText, setSermonText] = useState('');
  const [result, setResult] = useState<SermonSupportResultData | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [fileStatus, setFileStatus] = useState(DEFAULT_FILE_STATUS);
  const [errorMessage, setErrorMessage] = useState('');

  const [activeTab, setActiveTab] = useState<SupportTabKey>('summary');
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    axios
      .post(`${MainURL}/admin/countup`, { date: `${yyyy}-${mm}-${dd}`, type: 'aiservicemenu' })
      .catch(() => {});
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  const resultCardRef = useRef<HTMLDivElement>(null);

  const handleScrollToUpload = () => {
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ensureLoggedIn = () => {
    if (!isLogin || !localStorage.getItem('refreshToken')) {
      alert('로그인이 필요합니다.');
      return false;
    }
    return true;
  };

  const handleSermonTextChange = (value: string) => {
    if (!ensureLoggedIn()) return;
    setErrorMessage('');
    setSermonText(value);
  };

  const uploadFile = async (file: File) => {
    if (!ensureLoggedIn()) return;

    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage('파일 용량이 너무 큽니다. 30MB 이하 파일을 업로드해주세요.');
      return;
    }

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setErrorMessage('PDF, DOCX, TXT 파일만 업로드할 수 있습니다.');
      return;
    }

    setErrorMessage('');
    setIsUploading(true);
    setFileStatus(`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)}MB · 텍스트 추출 중...`);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${MainURL}/api/sermon/upload`, formData);
      setSermonText(res.data.text);
      setFileStatus(`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)}MB · 텍스트 추출 완료`);
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      const message = axios.isAxiosError(error) && error.response?.data?.error
        ? error.response.data.error
        : '파일에서 텍스트를 추출하지 못했습니다. 잠시 후 다시 시도해주세요.';
      setErrorMessage(message);
      setFileStatus(DEFAULT_FILE_STATUS);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleGenerate = async () => {
    if (!sermonText.trim()) {
      setErrorMessage('설교 본문을 업로드하거나 입력해주세요.');
      return;
    }

    if (!ensureLoggedIn()) return;

    const verifyToken = localStorage.getItem('refreshToken');

    setIsGenerating(true);
    setErrorMessage('');
    setResult(null);
    setActiveTab('summary');

    try {
      const res = await axios.post(`${MainURL}/api/sermon/summary`, {
        sermonText,
        verifyToken,
      });
      const { summary, qt, groupShare, praiseThemes } = res.data;
      const matchedThemes: string[] = praiseThemes ?? [];
      const praiseSongs = await fetchSongsByThemes(matchedThemes);

      setResult({ summary, qt, groupShare, praiseThemes: matchedThemes, praiseSongs });
    } catch (error) {
      console.error('설교지원 자료 생성 오류:', error);
      const message = axios.isAxiosError(error) && error.response?.data?.error
        ? error.response.data.error
        : 'AI 자료 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      setErrorMessage(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTabChange = (tab: SupportTabKey) => {
    setIsEditing(false);
    setActiveTab(tab);
  };

  const handleStartEdit = () => {
    if (!result || activeTab === 'praise') return;
    setDraftText(result[activeTab] ?? '');
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (!result || activeTab === 'praise') return;
    setResult({ ...result, [activeTab]: draftText });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleCopy = async () => {
    if (!result || activeTab === 'praise') return;
    const text = result[activeTab] ?? '';
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 1500);
    } catch (error) {
      console.error('복사 오류:', error);
      setErrorMessage('복사에 실패했습니다. 브라우저 권한을 확인해주세요.');
    }
  };

  const handleWordDownload = () => {
    if (!result || activeTab === 'praise') return;
    const label = SUPPORT_TAB_LABELS[activeTab];
    const bodyHtml = `<p>${escapeHtml(result[activeTab] ?? '').replace(/\n/g, '<br/>')}</p>`;

    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset="utf-8"><title>${escapeHtml(label)}</title></head>
      <body><h1>${escapeHtml(label)}</h1>${bodyHtml}</body>
      </html>`;

    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${label}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImageSave = async () => {
    if (!resultCardRef.current || activeTab === 'praise') return;
    const label = SUPPORT_TAB_LABELS[activeTab];

    try {
      const canvas = await html2canvas(resultCardRef.current, { backgroundColor: '#ffffff', scale: 2 });
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${label}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('이미지 저장 오류:', error);
      setErrorMessage('이미지 저장에 실패했습니다.');
    }
  };

  return {
    isLogin,
    sermonText, handleSermonTextChange,
    result,
    isUploading, isGenerating, isDragging,
    fileStatus, errorMessage,
    isEditing, draftText, setDraftText,
    copyFeedback,
    activeTab, handleTabChange,
    fileInputRef, uploadSectionRef, resultCardRef,
    handleScrollToUpload,
    handleFileChange, handleDragOver, handleDragLeave, handleDrop,
    handleGenerate,
    handleStartEdit, handleSaveEdit, handleCancelEdit,
    handleCopy, handleWordDownload, handleImageSave,
  };
}
